// Auth context. Fetches /auth/me on mount; exposes user + helpers.

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthMe } from '@command-floor/shared-types';
import { api, ApiError } from './api.js';

interface AuthContextValue {
  user: AuthMe | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await api.get<AuthMe>('/auth/me');
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setUser(null);
      } else {
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await api.post('/auth/logout');
    setUser(null);
    window.location.assign('/login');
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** UX-only permission helpers. Server still does the real enforcement. */
export function useCanCreateTable(): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return user.role === 'LEADERSHIP' || user.role === 'ENTITY_MANAGER';
}
